import {
  ID,
  ListApiResponse,
  QueryFilters,
  SingleApiResponse,
  SuccessApiResponse
} from '@ianesthesie-kit/services';
import {
  CcamModifier,
  ConventionStatus,
  CreateInvoicePayload,
  HospitalProcedureAnesthesiaPrice,
  HospitalProcedureAnesthesiaPricePerProcedureCode,
  InsurancePlan,
  Invoice,
  ProcedureReimbursementInfo
} from '../billing/billing.model';
import axios, { AxiosResponse } from 'axios';
import { captureThrowOnDev } from '@/util';
import { stringifyParams } from './billing.queryParams';

export class BillingService {
  private static INSTANCE_BY_ID: BillingService;
  private static BASE_URL = '/billing';

  private readonly patientId?: ID;

  private readonly url: string;

  private constructor(patientId: string) {
    this.url = `${BillingService.BASE_URL}/${patientId}/invoice`;
    this.patientId = patientId;
  }

  public static fetchInsurancePlans(
    params: QueryFilters
  ): Promise<InsurancePlan[]> {
    const qs = stringifyParams(params);
    return axios
      .get<ListApiResponse<InsurancePlan>>(
        `${this.BASE_URL}/insurance-plan${qs}`
      )
      .then((resp) => resp.data.data)
      .then((plans) =>
        plans.map((p) => ({
          ...p,
          hospitalizationWithOptam:
            p.hospitalizationWithOptam && p.hospitalizationWithOptam / 100,
          hospitalizationWithoutOptam:
            p.hospitalizationWithoutOptam &&
            p.hospitalizationWithoutOptam / 100,
          consultationWithOptam:
            p.consultationWithOptam && p.consultationWithOptam / 100,
          consultationWithoutOptam:
            p.consultationWithoutOptam && p.consultationWithoutOptam / 100
        }))
      );
  }

  public static fetchReimbursementInfos(
    params: QueryFilters
  ): Promise<ProcedureReimbursementInfo[]> {
    const qs = stringifyParams(params);
    return axios
      .get<ListApiResponse<ProcedureReimbursementInfo>>(
        `${this.BASE_URL}/procedure-reimbursement-info${qs}`
      )
      .then((resp) => resp.data.data);
  }

  public static fetchHospitalAnesthesiaCosts(
    params?: QueryFilters
  ): Promise<HospitalProcedureAnesthesiaPricePerProcedureCode> {
    const qs = stringifyParams({ pageSize: 1000, ...params });
    return axios
      .get<ListApiResponse<HospitalProcedureAnesthesiaPrice>>(
        `${this.BASE_URL}/procedure-anesthesia-price${qs}`
      )
      .then((resp) =>
        resp.data.data.reduce((acc, dto) => {
          acc[dto.procedureReimbursementInfo.code] = dto.price;
          return acc;
        }, {} as HospitalProcedureAnesthesiaPricePerProcedureCode)
      );
  }

  public static fetchHospitalAnesthesiaCostsByCcam(
    ccamCodes: string[]
  ): Promise<HospitalProcedureAnesthesiaPricePerProcedureCode> {
    const qs = stringifyParams({ ccam: ccamCodes });
    return axios
      .get<ListApiResponse<HospitalProcedureAnesthesiaPrice>>(
        `${this.BASE_URL}/procedure-anesthesia-price${qs}`
      )
      .then((resp) =>
        resp.data.data.reduce((acc, dto) => {
          acc[dto.procedureReimbursementInfo.code] = dto.price;
          return acc;
        }, {} as HospitalProcedureAnesthesiaPricePerProcedureCode)
      );
  }

  public static fetchModifiers(): Promise<CcamModifier[]> {
    return axios
      .get<{ data: CcamModifier[] }>(`${BillingService.BASE_URL}/ccam-modifier`)
      .then((resp) => resp.data.data);
  }

  public static updateConventionStatus(
    status: ConventionStatus
  ): Promise<void> {
    return axios
      .put('/auth/me/convention-status', { conventionStatus: status })
      .then(() => undefined);
  }

  public static getEligibleBrssRateForIndex(index: number) {
    // in case of multiple procedures, public insurance coverage differs
    switch (index) {
      case 0: {
        return 1;
      }
      case 1: {
        return 0.5;
      }
      case 2: {
        return 0.75;
      }
      default: {
        captureThrowOnDev(
          `Up to 3 procedure codes are supported. Found ${index + 1}`
        );
        return 1;
      }
    }
  }

  public static getInstance(patientId: ID): BillingService {
    if (this.shouldCreateNewInstance(this.INSTANCE_BY_ID, patientId)) {
      this.INSTANCE_BY_ID = new BillingService(patientId);
    }
    return this.INSTANCE_BY_ID;
  }

  private static shouldCreateNewInstance(
    instance: BillingService,
    patientId: ID
  ): boolean {
    if (!patientId) {
      throw new Error('Patient id must be provided');
    }
    return !instance || instance.patientId !== patientId;
  }

  /** The query string of both quote generation requests. */
  private static invoiceParams({
    sendToPatient,
    includeInvoicePage
  }: Pick<CreateInvoicePayload, 'sendToPatient' | 'includeInvoicePage'>) {
    let params = `?send=${sendToPatient === true ? 'true' : 'false'}`;
    if (includeInvoicePage !== undefined) {
      params += `&include-invoice-page=${includeInvoicePage}`;
    }
    return params;
  }

  public fetch(params: QueryFilters = {}): Promise<ListApiResponse<Invoice>> {
    const qs = stringifyParams(params);
    return axios
      .get(`${this.url}${qs}`)
      .then(
        (response: AxiosResponse<ListApiResponse<Invoice>>) => response.data
      );
  }

  public generate({
    sendToPatient,
    includeInvoicePage,
    ...payload
  }: CreateInvoicePayload): Promise<SingleApiResponse<Invoice>> {
    const params = BillingService.invoiceParams({
      sendToPatient,
      includeInvoicePage
    });
    return axios
      .post(`${this.url}${params}`, payload)
      .then(
        (response: AxiosResponse<SingleApiResponse<Invoice>>) => response.data
      );
  }

  /** Issues the quote of a surgery planned at this consultation, before its event exists. */
  public generateForPlan(
    planId: ID,
    { sendToPatient, includeInvoicePage, ...payload }: CreateInvoicePayload
  ): Promise<SingleApiResponse<Invoice>> {
    const params = BillingService.invoiceParams({
      sendToPatient,
      includeInvoicePage
    });
    return axios
      .post(
        `${BillingService.BASE_URL}/${this.patientId}/follow-up-plan/${planId}/invoice${params}`,
        payload
      )
      .then(
        (response: AxiosResponse<SingleApiResponse<Invoice>>) => response.data
      );
  }

  public sendToPatient(invoice: Invoice): Promise<SuccessApiResponse> {
    return axios
      .post(`${this.url}/${invoice.id}/send`)
      .then((response: AxiosResponse<SuccessApiResponse>) => response.data);
  }

  public signOnSite(invoiceId: number | string): Promise<SuccessApiResponse> {
    return axios
      .post(`${this.url}/${invoiceId}/sign-on-site`)
      .then((response: AxiosResponse<SuccessApiResponse>) => response.data);
  }
}
